import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowPathIcon,
  BellIcon,
  BellSlashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useUnreadNotificationCount } from "../../hooks/useUnreadNotificationCount";
import { notificationService } from "../../services/notificationService";
import { NotificationPermissionState, FCMTokenData } from "../../types/user";

type PushStatus =
  | "loading"
  | "blocked-unregistered"
  | "allowed-unregistered"
  | "blocked-registered"
  | "allowed-registered"
  | "allowed-stale";

// Whether the token Firestore holds for this device still matches the live one.
// "unknown" means we could not find out, which must not be treated as a fault.
type TokenHealth = "unknown" | "current" | "stale";

const OPEN_DIALOG_ON_NAV_KEY = "open_push_status_dialog_on_nav";

const defaultPermissionState: NotificationPermissionState = {
  granted: false,
  denied: false,
  default: true,
};

const PushNotificationStatusIndicator: React.FC = () => {
  const { currentUser } = useAuth();
  const { unreadCount, loading: unreadLoading } = useUnreadNotificationCount();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [hasShakeelServiceWorker, setHasShakeelServiceWorker] = useState(false);
  const [permissionState, setPermissionState] =
    useState<NotificationPermissionState>(defaultPermissionState);
  const [currentDeviceToken, setCurrentDeviceToken] =
    useState<FCMTokenData | null>(null);
  const [tokenHealth, setTokenHealth] = useState<TokenHealth>("unknown");
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const checkShakeelServiceWorker = useCallback(async (): Promise<boolean> => {
    if (!("serviceWorker" in navigator)) return false;

    try {
      const registration = await navigator.serviceWorker.ready;
      const targetWorker =
        navigator.serviceWorker.controller ||
        registration.active ||
        registration.waiting;

      if (!targetWorker) return false;

      return await new Promise<boolean>((resolve) => {
        const timeout = window.setTimeout(() => {
          navigator.serviceWorker.removeEventListener("message", handleMessage);
          resolve(false);
        }, 1500);

        const handleMessage = (event: MessageEvent) => {
          if (event.data?.type !== "SHAKEEL_SW_STATUS") return;
          window.clearTimeout(timeout);
          navigator.serviceWorker.removeEventListener("message", handleMessage);
          resolve(Boolean(event.data?.hasShakeelServiceWorker));
        };

        navigator.serviceWorker.addEventListener("message", handleMessage);
        targetWorker.postMessage({ type: "CHECK_SHAKEEL_SW" });
      });
    } catch (error) {
      console.warn("Could not verify Shakeel service worker flag:", error);
      return false;
    }
  }, []);

  const refreshStatus = useCallback(async () => {
    if (!currentUser?.uid) {
      setIsCheckingStatus(false);
      return;
    }

    setIsCheckingStatus(true);
    setErrorMessage(null);

    try {
      const swStatus = await checkShakeelServiceWorker();
      setHasShakeelServiceWorker(swStatus);

      const permission = notificationService.getPermissionState();
      setPermissionState(permission);

      const tokens = await notificationService.getUserFCMTokens(
        currentUser.uid,
      );
      const deviceId = notificationService.getCurrentDeviceId();
      const match = tokens.find((token) => token.deviceId === deviceId) || null;
      setCurrentDeviceToken(match);

      if (permission.granted) {
        // On app/header load, perform a lightweight readiness check in the background.
        await notificationService.waitForMessagingReady();

        // The device id lives in localStorage and survives a service worker
        // reset, but the push subscription behind the token does not. Matching
        // on deviceId alone would therefore report a dead registration as
        // healthy, so compare against the live token.
        try {
          const liveToken = await notificationService.getFCMToken();
          if (!liveToken || !match) {
            setTokenHealth("unknown");
          } else {
            setTokenHealth(match.token === liveToken ? "current" : "stale");
          }
        } catch (tokenError) {
          console.warn("Could not verify FCM token freshness:", tokenError);
          setTokenHealth("unknown");
        }
      } else {
        setTokenHealth("unknown");
      }
    } catch (error) {
      console.error("Failed to check push notification status:", error);
      setErrorMessage("Unable to check push notification status right now.");
    } finally {
      setIsCheckingStatus(false);
    }
  }, [checkShakeelServiceWorker, currentUser?.uid]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const status: PushStatus = useMemo(() => {
    if (isCheckingStatus) return "loading";
    const isRegistered = !!currentDeviceToken;

    if (!permissionState.granted && !isRegistered)
      return "blocked-unregistered";
    if (permissionState.granted && !isRegistered) return "allowed-unregistered";
    if (!permissionState.granted && isRegistered) return "blocked-registered";
    if (tokenHealth === "stale") return "allowed-stale";
    return "allowed-registered";
  }, [
    currentDeviceToken,
    isCheckingStatus,
    permissionState.granted,
    tokenHealth,
  ]);

  const shouldOpenSetupDialog = useMemo(() => {
    // Open details only when setup is incomplete.
    if (isCheckingStatus) return false;
    return status !== "allowed-registered" || !hasShakeelServiceWorker;
  }, [hasShakeelServiceWorker, isCheckingStatus, status]);

  useEffect(() => {
    const shouldOpenDialog = sessionStorage.getItem(OPEN_DIALOG_ON_NAV_KEY);
    if (shouldOpenDialog !== "1") {
      return;
    }

    // Wait for checks to finish before deciding to open/clear.
    if (isCheckingStatus) {
      return;
    }

    if (shouldOpenSetupDialog) {
      setIsDialogOpen(true);
    }
    sessionStorage.removeItem(OPEN_DIALOG_ON_NAV_KEY);
  }, [isCheckingStatus, shouldOpenSetupDialog]);

  const statusMeta = useMemo(() => {
    switch (status) {
      case "loading":
        return {
          label: "Checking push setup",
          title: "Checking push notification setup",
          icon: <BellIcon className="size-6 animate-pulse" />,
          colorClass: "text-base-content/50",
        };
      case "blocked-unregistered":
        return {
          label: "Push blocked and not registered",
          title: "Push blocked and device not registered",
          icon: <BellSlashIcon className="size-6" />,
          colorClass: "text-red-600 dark:text-red-400",
        };
      case "allowed-unregistered":
        return {
          label: "Push allowed but not registered",
          title: "Push allowed but device not registered",
          icon: <ExclamationTriangleIcon className="size-6" />,
          colorClass: "text-amber-600 dark:text-amber-400",
        };
      case "blocked-registered":
        return {
          label: "Push blocked but registered",
          title: "Push blocked but device is registered",
          icon: <BellSlashIcon className="size-6" />,
          colorClass: "text-amber-700 dark:text-amber-400",
        };
      case "allowed-stale":
        return {
          label: "Push registration out of date",
          title: "This device's push registration is out of date",
          icon: <ExclamationTriangleIcon className="size-6" />,
          colorClass: "text-amber-600 dark:text-amber-400",
        };
      case "allowed-registered":
      default:
        return {
          label: "Push ready",
          title: "Push notifications are active on this device",
          icon: <BellIcon className="size-6" />,
          colorClass: "text-base-content/50",
        };
    }
  }, [status]);

  const indicatorClasses = useMemo(() => {
    if (isCheckingStatus) {
      return "text-base-content/50 hover:text-base-content/60";
    }
    if (!hasShakeelServiceWorker) {
      return "text-amber-700 dark:text-amber-400 hover:text-amber-600 dark:hover:text-amber-300";
    }
    return `${statusMeta.colorClass} hover:text-base-content/60`;
  }, [hasShakeelServiceWorker, isCheckingStatus, statusMeta.colorClass]);

  const handleIndicatorClick = useCallback(() => {
    if (shouldOpenSetupDialog) {
      sessionStorage.setItem(OPEN_DIALOG_ON_NAV_KEY, "1");
      setIsDialogOpen(true);
    }
    navigate("/notifications");
  }, [navigate, shouldOpenSetupDialog]);

  const handleAllowNotifications = useCallback(async () => {
    if (!currentUser?.uid) return;

    setIsActionLoading(true);
    setErrorMessage(null);
    setFeedbackMessage(null);

    try {
      const granted = await notificationService.requestPermission();
      if (!granted) {
        setFeedbackMessage("Permission was not granted.");
      } else {
        await notificationService.addFCMTokenToUser(currentUser.uid, true);
        setFeedbackMessage(
          "Notification permission granted and this device is now registered.",
        );
      }
      await refreshStatus();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to request permission.";
      setErrorMessage(message);
    } finally {
      setIsActionLoading(false);
    }
  }, [currentUser?.uid, refreshStatus]);

  const handleRegisterDevice = useCallback(async () => {
    if (!currentUser?.uid) return;
    setIsActionLoading(true);
    setErrorMessage(null);
    setFeedbackMessage(null);

    try {
      await notificationService.addFCMTokenToUser(currentUser.uid, true);
      setFeedbackMessage(
        "This device is now registered for push notifications.",
      );
      await refreshStatus();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to register this device.";
      setErrorMessage(message);
    } finally {
      setIsActionLoading(false);
    }
  }, [currentUser?.uid, refreshStatus]);

  const handleDisableDeviceRecord = useCallback(async () => {
    if (!currentUser?.uid || !currentDeviceToken?.deviceId) return;
    setIsActionLoading(true);
    setErrorMessage(null);
    setFeedbackMessage(null);

    try {
      await notificationService.toggleDeviceNotifications(
        currentUser.uid,
        currentDeviceToken.deviceId,
        false,
      );
      setFeedbackMessage("Disabled this device record in Firebase.");
      await refreshStatus();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to disable this device in Firebase.";
      setErrorMessage(message);
    } finally {
      setIsActionLoading(false);
    }
  }, [currentDeviceToken?.deviceId, currentUser?.uid, refreshStatus]);

  return (
    <>
      <button
        type="button"
        onClick={handleIndicatorClick}
        className={`relative inline-flex items-center justify-center rounded-full p-1 transition-colors ${indicatorClasses}`}
        title={statusMeta.title}
        aria-label={statusMeta.label}
      >
        {statusMeta.icon}
        {!unreadLoading && unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      <Transition appear show={isDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[70]" onClose={setIsDialogOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto p-4">
            <div className="flex min-h-full items-center justify-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md rounded-xl bg-base-100 p-8 shadow-xl">
                  <div className="flex items-start justify-between">
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-base-content">
                        Push Notifications
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-base-content/70">
                        {statusMeta.title}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsDialogOpen(false)}
                      className="rounded-md p-1 text-base-content/50 hover:bg-base-200 hover:text-base-content/70"
                      aria-label="Close push status dialog"
                    >
                      <XMarkIcon className="size-5" />
                    </button>
                  </div>

                  <div className="mt-4 space-y-3 text-sm">
                    {hasShakeelServiceWorker ? (
                      <div className="rounded-md bg-emerald-500/10 p-2 text-emerald-700 dark:text-emerald-400">
                        SSW current
                      </div>
                    ) : (
                      <div className="rounded-md bg-red-500/10 p-2 text-red-700 dark:text-red-400">
                        You need to uninstall and reinstall the app to get the
                        latest push notification updates
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <CheckCircleIcon
                        className={`size-4 ${
                          permissionState.granted
                            ? "text-emerald-600"
                            : "text-base-content/30"
                        }`}
                      />
                      <span className="text-base-content/80">
                        Browser permission:{" "}
                        <span className="font-medium">
                          {permissionState.granted
                            ? "Allowed"
                            : permissionState.denied
                              ? "Blocked"
                              : "Not decided"}
                        </span>
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <CheckCircleIcon
                        className={`size-4 ${
                          !currentDeviceToken
                            ? "text-base-content/30"
                            : tokenHealth === "stale"
                              ? "text-amber-600"
                              : "text-emerald-600"
                        }`}
                      />
                      <span className="text-base-content/80">
                        Device registration:{" "}
                        <span className="font-medium">
                          {!currentDeviceToken
                            ? "Not registered"
                            : tokenHealth === "stale"
                              ? "Registered (out of date)"
                              : "Registered"}
                        </span>
                      </span>
                    </div>
                  </div>

                  {(status === "blocked-unregistered" ||
                    status === "blocked-registered") && (
                    <div className="mt-4 rounded-md bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
                      If notifications are blocked, enable them in your browser
                      settings, then click re-check.
                    </div>
                  )}

                  {status === "allowed-stale" && (
                    <div className="mt-4 rounded-md bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
                      This device's push registration is out of date, so
                      notifications may not arrive. Re-register this device to
                      fix it.
                    </div>
                  )}

                  {feedbackMessage && (
                    <div className="mt-4 rounded-md bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-300">
                      {feedbackMessage}
                    </div>
                  )}

                  {errorMessage && (
                    <div className="mt-4 rounded-md bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-400">
                      {errorMessage}
                    </div>
                  )}

                  <div className="mt-5 flex flex-wrap gap-2">
                    {!permissionState.granted && !permissionState.denied && (
                      <button
                        type="button"
                        onClick={handleAllowNotifications}
                        disabled={isActionLoading}
                        className="rounded-md bg-neutral px-3 py-2 text-sm font-medium text-neutral-content hover:bg-neutral/90 disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        Allow Notifications
                      </button>
                    )}

                    {permissionState.granted &&
                      (!currentDeviceToken || tokenHealth === "stale") && (
                        <button
                          type="button"
                          onClick={handleRegisterDevice}
                          disabled={isActionLoading}
                          className="rounded-md bg-neutral px-3 py-2 text-sm font-medium text-neutral-content hover:bg-neutral/90 disabled:cursor-not-allowed disabled:opacity-70"
                        >
                          {currentDeviceToken
                            ? "Re-register This Device"
                            : "Register This Device"}
                        </button>
                      )}

                    {status === "blocked-registered" &&
                      currentDeviceToken?.deviceId && (
                        <button
                          type="button"
                          onClick={handleDisableDeviceRecord}
                          disabled={isActionLoading}
                          className="rounded-md bg-base-100 px-3 py-2 text-sm font-medium text-base-content/80 ring-1 ring-base-300 hover:bg-base-200 disabled:cursor-not-allowed disabled:opacity-70"
                        >
                          Disable Device Record
                        </button>
                      )}

                    <button
                      type="button"
                      onClick={refreshStatus}
                      disabled={isActionLoading}
                      className="inline-flex items-center gap-1 rounded-md bg-base-100 px-3 py-2 text-sm font-medium text-base-content/80 ring-1 ring-base-300 hover:bg-base-200 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      <ArrowPathIcon
                        className={`size-4 ${isCheckingStatus ? "animate-spin" : ""}`}
                      />
                      Re-check
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default React.memo(PushNotificationStatusIndicator);
